import React, { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  styled,
  Tooltip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import useGetAppsQuery from "../../useQuery/useGetAppsQuery";
import useDeleteAppMutation from "../../useQuery/useDeleteAppMutation";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../../hooks";

// Styled Components
const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const StyledSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  background-color: #ffffff;
  font-size: 16px;
  color: #2b3539;
  min-width: 200px;
  max-height: 300px;

  .MuiSelect-select {
    padding: 13px 15px;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 5px;
    border: 1px solid #d1dbff;
    background-color: #ffffff;
    color: #2b3539;

    &:focus {
      border-color: #2550d1;
      box-shadow: 0 0 0 2px rgba(37, 80, 209, 0.1);
    }
  }
`;

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  marginBottom: theme.spacing(1.5),
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.03)",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: "#eef1f8",
    borderColor: "rgba(37, 80, 209, 0.1)",
  },
}));

const FormField = ({ label, tooltip, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <IconButton size="small" sx={{ ml: 0.5, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);

const AppVersionManager = ({ products = [], onCreateAppVersion, isCreating = false }) => {
  const queryClient = useQueryClient();
  const notify = useNotification();

  const [editingApp, setEditingApp] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [platform, setPlatform] = useState("android");
  const [appLocation, setAppLocation] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [appPackage, setAppPackage] = useState("");
  const [appActivity, setAppActivity] = useState("");
  const [isLatestVersion, setIsLatestVersion] = useState(false);
  const [enableAutoOpen, setEnableAutoOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);

  const hasChanges = () => {
    if (!editingApp) return true;

    const locationChanged = appLocation !== (editingApp.appLocation || "");
    const versionChanged = appVersion !== editingApp.appVersion;
    const packageChanged = appPackage !== (editingApp.appPackage || "");
    const activityChanged = appActivity !== (editingApp.appActivity || "");
    const latestChanged = isLatestVersion !== editingApp.isLatest;
    const editingPlatform = editingApp.platform || "android";
    // Auto-Open toggle only applies to Android (gates Package/Activity there).
    // iOS always shows Bundle Identifier, so there's nothing to gate.
    const autoOpenChanged =
      editingPlatform === "android"
        ? enableAutoOpen !== !!(editingApp.appActivity && editingApp.appPackage)
        : false;
    const platformChanged = platform !== editingPlatform;

    return (
      locationChanged ||
      versionChanged ||
      packageChanged ||
      activityChanged ||
      latestChanged ||
      autoOpenChanged ||
      platformChanged
    );
  };

  const deleteAppMutation = useDeleteAppMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apps", selectedProduct] });
      notify("App version deleted successfully!", "success");
    },
    onError: (error) => {
      notify("Failed to delete app version", "error");
    },
  });

  const { data: appsData, isLoading: isLoadingApps } = useGetAppsQuery(selectedProduct, {
    enabled: !!selectedProduct,
  });

  const appVersions = React.useMemo(() => {
    if (!Array.isArray(appsData)) return [];

    return appsData.map((app) => ({
      id: app.appId,
      versionName: app.appVersion,
      appVersion: app.appVersion,
      appLocation: app.appLocation,
      appActivity: app.appActivity,
      appPackage: app.appPackage,
      productId: app.productId,
      productType: app.productType,
      platform: app.platform || "android",
      isLatest: app.isLatestVersion === "yes",
    }));
  }, [appsData]);

  const handleCreateAppVersion = async () => {
    // Validate required fields based on enableAutoOpen state
    if (!selectedProduct || !appVersion.trim()) {
      notify("Please fill in all required fields", "error");
      return;
    }

    if (platform === "ios") {
      if (!appPackage.trim()) {
        notify("Please fill in the Bundle Identifier", "error");
        return;
      }
      // Bundle IDs follow reverse-DNS format (e.g. com.apple.mobilesafari).
      // This catches common mistakes like entering a friendly app name instead.
      const bundleIdPattern = /^[A-Za-z0-9]+(\.[A-Za-z0-9-]+)+$/;
      if (!bundleIdPattern.test(appPackage.trim())) {
        notify(
          "Bundle Identifier should be in reverse-DNS format, e.g. com.apple.mobilesafari — not a friendly app name",
          "error",
        );
        return;
      }
    } else if (enableAutoOpen) {
      if (!appPackage.trim()) {
        notify("Please fill in App Package when auto-open is enabled", "error");
        return;
      }
      if (!appActivity.trim()) {
        notify("Please fill in App Activity when auto-open is enabled", "error");
        return;
      }
    }

    if (editingApp && !hasChanges()) {
      notify("No changes detected", "info");
      return;
    }

    try {
      const payload = {
        productId: selectedProduct,
        appLocation: appLocation.trim(),
        appVersion: appVersion.trim(),
        isLatestVersion: isLatestVersion ? "yes" : "no",
        productType: "mobile",
        platform: platform,
      };

      if (platform === "ios") {
        // Bundle identifier is always required for iOS to identify/attach to the app
        payload.appPackage = appPackage.trim();
      } else if (enableAutoOpen) {
        // Android package/activity are only needed when auto-open is enabled
        payload.appPackage = appPackage.trim();
        payload.appActivity = appActivity.trim();
      }

      if (editingApp) {
        payload.appId = editingApp.id;
      }

      await onCreateAppVersion(payload);

      notify(
        editingApp ? "App version updated successfully" : "App version created successfully",
        "success",
      );

      setAppLocation("");
      setAppActivity("");
      setAppVersion("");
      setAppPackage("");
      setIsLatestVersion(false);
      setEnableAutoOpen(false);
      setPlatform("android");
      setEditingApp(null);

      queryClient.invalidateQueries({ queryKey: ["apps", selectedProduct] });
    } catch (error) {
      const errorData = error?.response?.data;

      if (errorData && errorData.includes("App version already exists")) {
        notify("App version already exists", "error");
      } else {
        notify("Failed to create app version", "error");
      }
    }
  };

  const handleEditAppVersion = (app) => {
    setSelectedProduct(app.productId);
    setAppLocation(app.appLocation);
    setAppActivity(app.appActivity || "");
    setAppVersion(app.appVersion);
    setAppPackage(app.appPackage || "");
    setIsLatestVersion(app.isLatest);
    const editingPlatform = app.platform || "android";
    setPlatform(editingPlatform);
    // Auto-Open toggle is only meaningful for Android; iOS always shows Bundle Identifier
    setEnableAutoOpen(
      editingPlatform === "android" ? !!(app.appActivity && app.appPackage) : false,
    );
    setEditingApp(app);
  };

  const handleDeleteAppVersion = (appVersion, productId) => {
    setVersionToDelete({ appVersion, productId });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (versionToDelete) {
      deleteAppMutation.mutate({
        appVersion: versionToDelete.appVersion,
        productId: versionToDelete.productId,
      });
    }
    setDeleteDialogOpen(false);
    setVersionToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setVersionToDelete(null);
  };

  const renderAppVersionForm = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#232d3d" }}>
        {editingApp ? "Edit App Version" : "Create App Version"}
      </Typography>

      <FormField label="Select Product*">
        <StyledSelect
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          displayEmpty
          fullWidth
          size="small"
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 320,
              },
            },
          }}
        >
          <MenuItem disabled value="">
            Select Product
          </MenuItem>
          {products?.map((product) => (
            <MenuItem key={product.idproducts} value={product.idproducts}>
              {product.productname}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormField>

      <FormField label="Platform*">
        <StyledSelect
          value={platform}
          onChange={(e) => {
            const newPlatform = e.target.value;
            setPlatform(newPlatform);
            if (newPlatform === "ios") {
              // Activity only applies to Android
              setAppActivity("");
            } else {
              // Package was for bundle id — clear it unless auto-open re-enables it
              if (!enableAutoOpen) {
                setAppPackage("");
              }
            }
          }}
          fullWidth
          size="small"
        >
          <MenuItem value="android">Android</MenuItem>
          <MenuItem value="ios">iOS</MenuItem>
        </StyledSelect>
      </FormField>

      <FormField label="App Location (Optional)">
        <StyledTextField
          fullWidth
          value={appLocation}
          onChange={(e) => setAppLocation(e.target.value)}
          placeholder="Enter the full path to your app file"
          variant="outlined"
          size="small"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {platform === "android"
            ? "Enter the full path to your app file (e.g., /Users/username/apps/myapp.apk)"
            : "Enter the full path to your app file (e.g., /Users/username/apps/myapp.ipa or .app)"}
        </Typography>
      </FormField>

      <FormField label="App Version*">
        <StyledTextField
          fullWidth
          value={appVersion}
          onChange={(e) => setAppVersion(e.target.value)}
          placeholder="E.g. 1.0.0"
          variant="outlined"
          size="small"
        />
      </FormField>

      {platform === "ios" && (
        <FormField
          label="Bundle Identifier*"
          tooltip="Required to identify and launch this app on an iOS device or simulator."
        >
          <StyledTextField
            fullWidth
            value={appPackage}
            onChange={(e) => setAppPackage(e.target.value)}
            placeholder="E.g. com.apple.mobilesafari"
            variant="outlined"
            size="small"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Enter the reverse-DNS bundle identifier of your iOS app (not the app's display name).
            This is required to attach an automation session to the app, whether or not it's opened
            automatically.
          </Typography>
        </FormField>
      )}

      <FormField
        label="Auto-Open App"
        tooltip={
          platform === "android"
            ? "When enabled, the app will automatically open during recording. If disabled, you'll need to manually open the app."
            : "When enabled, the app will automatically open during recording. If disabled, you'll need to manually open the app on the device/simulator first."
        }
      >
        <FormControlLabel
          control={
            <Switch
              checked={enableAutoOpen}
              onChange={(e) => {
                setEnableAutoOpen(e.target.checked);

                if (!e.target.checked && platform === "android") {
                  setAppActivity("");
                  setAppPackage("");
                }
              }}
              color="primary"
            />
          }
          label="Enable automatic app launch"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {platform === "android"
            ? "Provide app package and activity details to automatically launch the app when starting a recording session"
            : "The app must already be installed on the device or simulator for auto-launch to work"}
        </Typography>
      </FormField>

      {platform === "android" && enableAutoOpen && (
        <>
          <FormField label="App Package*">
            <StyledTextField
              fullWidth
              value={appPackage}
              onChange={(e) => setAppPackage(e.target.value)}
              placeholder="E.g. com.example.app"
              variant="outlined"
              size="small"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Enter the package name of your Android app
            </Typography>
          </FormField>

          <FormField label="App Activity*">
            <StyledTextField
              fullWidth
              value={appActivity}
              onChange={(e) => setAppActivity(e.target.value)}
              placeholder="E.g. com.example.MainActivity"
              variant="outlined"
              size="small"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Enter the main activity class name for your Android app
            </Typography>
          </FormField>
        </>
      )}

      <FormField label="Version Settings">
        <FormControlLabel
          control={
            <Switch
              checked={isLatestVersion}
              onChange={(e) => setIsLatestVersion(e.target.checked)}
              color="primary"
            />
          }
          label="Mark as latest version"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          If enabled, this version will be marked as the latest version for the selected product
        </Typography>
      </FormField>

      <Box sx={{ mt: 3 }}>
        <StyledButton
          onClick={handleCreateAppVersion}
          disabled={
            !selectedProduct ||
            !appVersion.trim() ||
            (platform === "ios" && !appPackage.trim()) ||
            (platform === "android" && enableAutoOpen && !appPackage.trim()) ||
            (platform === "android" && enableAutoOpen && !appActivity.trim()) ||
            isCreating ||
            (editingApp && !hasChanges())
          }
          startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#2550d1",
            boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
            "&:hover": {
              backgroundColor: "#1e3fa1",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          {isCreating
            ? editingApp
              ? "Updating..."
              : "Creating..."
            : editingApp
              ? "Update App Version"
              : "Create App Version"}
        </StyledButton>
        {editingApp && (
          <Button
            onClick={() => {
              setEditingApp(null);
              setAppLocation("");
              setAppActivity("");
              setAppVersion("");
              setAppPackage("");
              setIsLatestVersion(false);
              setEnableAutoOpen(false);
              setPlatform("android");
            }}
            sx={{ ml: 2 }}
            variant="outlined"
          >
            Cancel Edit
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderAppVersionsList = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#232d3d" }}>
        App Versions
        {selectedProduct && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isLoadingApps ? "Loading..." : `${appVersions.length} version(s) found`}
          </Typography>
        )}
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          maxHeight: "calc(100vh - 250px)",
          pr: 1,
        }}
      >
        {!selectedProduct ? (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              backgroundColor: "#f8f9fc",
              borderRadius: "8px",
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
              Select a product to view app versions
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 400 }}>
              Choose a product from the dropdown to see its available app versions
            </Typography>
          </Box>
        ) : isLoadingApps ? (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              backgroundColor: "#f8f9fc",
              borderRadius: "8px",
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
              Loading app versions...
            </Typography>
          </Box>
        ) : appVersions.length > 0 ? (
          <List sx={{ px: 0 }}>
            {appVersions.map((version) => (
              <StyledListItem key={version.id}>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {version.versionName}
                      </Typography>
                      {version.isLatest && (
                        <Chip
                          size="small"
                          label="Latest"
                          color="primary"
                          sx={{ borderRadius: "6px" }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={`Version: ${version.appVersion}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      <Chip
                        size="small"
                        label={`Type: ${version.productType}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      <Chip
                        size="small"
                        label={`Platform: ${version.platform === "ios" ? "iOS" : "Android"}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      <Chip
                        size="small"
                        label={`${version.platform === "ios" ? "Bundle ID" : "Package"}: ${
                          version.appPackage || "N/A"
                        }`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      {version.platform !== "ios" && (
                        <Chip
                          size="small"
                          label={`Activity: ${version.appActivity || "N/A"}`}
                          sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Location: {version.appLocation || "Not specified"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        ID: {version.id}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Edit app version">
                    <IconButton
                      onClick={() => handleEditAppVersion(version)}
                      color="primary"
                      size="small"
                      sx={{
                        mr: 1,
                        "&:hover": {
                          backgroundColor: "rgba(37, 80, 209, 0.1)",
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete app version">
                    <IconButton
                      onClick={() => handleDeleteAppVersion(version.appVersion, version.productId)}
                      disabled={deleteAppMutation.isPending}
                      color="error"
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                        },
                      }}
                    >
                      {deleteAppMutation.isPending ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledListItem>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              backgroundColor: "#f8f9fc",
              borderRadius: "8px",
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
              No app versions found
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 400 }}>
              Create app versions to manage different versions of your mobile applications
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete App Version</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete app version "{versionToDelete?.appVersion}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderAppVersionForm()}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderAppVersionsList()}
        </Grid>
      </Grid>
    </>
  );
};

export default AppVersionManager;
